/**
 * Recognises a request that never reached a server: the browser's own `fetch`
 * (or XHR) rejected, so there is no status code, no response body and no
 * server-side trace — the machine was offline, asleep, behind a captive portal,
 * or the tab was torn down mid-flight.
 *
 * Every browser words this differently, and each wording arrives from a
 * different layer of our stack:
 *   - Chromium `fetch`           → "Failed to fetch"        (Sentry WAWIASSIST-1T2)
 *   - WebKit/Safari `fetch`      → "Load failed"            (Sentry WAWIASSIST-1GB)
 *   - Firefox `fetch`            → "NetworkError when attempting to fetch resource."
 *   - AWS Amplify's fetch handler wraps the browser `TypeError` into
 *     `NetworkError: A network error has occurred.`         (Sentry WAWIASSIST-246 / WA-3800)
 *
 * All of them mean the same thing to the user ("you are offline, retry") and
 * none of them is per-occurrence actionable for us, which is exactly the
 * treatment `responseHandler` already gives server-side connection drops
 * (WA-3546 / WA-3701). Amplify's own client middleware already retried the
 * request three times with jittered backoff before surfacing it, so a message
 * that reaches here is not a single dropped packet.
 */
export const BROWSER_NETWORK_FAILURE_PATTERNS: readonly RegExp[] = [
  /failed to fetch/i,
  /\bload failed\b/i,
  /a network error has occurred/i,
  /networkerror when attempting to fetch resource/i,
  /network request failed/i,
  /the network connection was lost/i,
  /the internet connection appears to be offline/i,
];

/**
 * Error *names* for the same class. Amplify tags its wrapped browser failures
 * with these (`NetworkError` from the fetch handler, `ERR_NETWORK` from the
 * Storage XHR handler — see `@aws-amplify/core`'s `defaultRetryDecider`), and a
 * navigation that kills an in-flight request surfaces as `AbortError` /
 * `TimeoutError`. Matching the name catches the case where the message has been
 * localised or reworded by the browser.
 */
const NETWORK_FAILURE_ERROR_NAMES: readonly string[] = [
  'NetworkError',
  'ERR_NETWORK',
  'AbortError',
  'TimeoutError',
];

/** Does this message text read as a browser-level network failure? */
export const isBrowserNetworkFailureMessage = (message: unknown): boolean =>
  typeof message === 'string' &&
  BROWSER_NETWORK_FAILURE_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Does this thrown value represent a browser-level network failure — by error
 * name or by message wording? Accepts `unknown` because it is used on `catch`
 * bindings and on RxJS error values, which are not typed as `Error`.
 */
export const isBrowserNetworkFailure = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return isBrowserNetworkFailureMessage(error);
  }

  const { name, message } = error as { name?: unknown; message?: unknown };

  if (typeof name === 'string' && NETWORK_FAILURE_ERROR_NAMES.includes(name)) {
    return true;
  }

  return isBrowserNetworkFailureMessage(message);
};
